import type { ReactNode } from 'react';

import Icon from '@/components/public/ui/Icon';

type FormAlertVariant = 'error' | 'success' | 'info';

type FormAlertProps = {
  variant?: FormAlertVariant;
  title: string;
  children?: ReactNode;
};

const TONES: Record<
  FormAlertVariant,
  { icon: string; panel: string; iconTile: string; role: 'alert' | 'status' }
> = {
  error: {
    icon: 'error',
    panel: 'border-error/30 bg-error/[0.07]',
    iconTile: 'bg-error/10 text-error',
    role: 'alert',
  },
  success: {
    icon: 'verified_user',
    panel: 'border-tertiary/30 bg-tertiary/[0.08]',
    iconTile: 'bg-tertiary/10 text-tertiary',
    role: 'status',
  },
  info: {
    icon: 'info',
    panel: 'border-primary/30 bg-primary/[0.08]',
    iconTile: 'bg-primary/10 text-primary',
    role: 'status',
  },
};

export function FormAlert({ variant = 'error', title, children }: FormAlertProps) {
  const tone = TONES[variant];

  return (
    <div
      role={tone.role}
      aria-live={tone.role === 'alert' ? 'assertive' : 'polite'}
      className={`flex items-start gap-3 rounded-xl border p-4 ${tone.panel}`}
    >
      <span
        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone.iconTile}`}
      >
        <Icon name={tone.icon} size="sm" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-headline text-on-surface text-base font-semibold">{title}</p>
        {children && (
          <div className="text-on-surface-light mt-1 text-sm leading-relaxed">{children}</div>
        )}
      </div>
    </div>
  );
}
